import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Table,
  TableBody,
  TableContainer,
  TableRow,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Switch,
  TablePagination,
  Pagination,
  Card,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

import { useTableState } from '../../../../hooks/useTableState';
import {
  corregirTexto,
  firstLetterUpperCase,
  hasPemissions,
  usarFuncionesComposicion,
} from '../../../../commons/utils';
import { ModalConfirmDeactivation } from '../../../../components/modals/ModalConfirmDeactivation';
import { ModalConfirmRestore } from '../../../../components/modals/ModalConfirmRestore';
import {
  StyledTableCell,
  StyledTableRow,
} from '../../../../commons/generics/StyledTableComponents';
import { StatusChip } from '../../../../commons/generics/StatusChip';
import { ClampedText } from '../../../../commons/generics/ClampedText';
import {
  getDevicesStore,
  getDevicesBySearch,
  storeDesactiveDevice,
  storeActiveDevice,
  getDevicesByPagination,
  storeDetailDevice,
} from '../../../../store/ratings/thunks';
import { GenericTableHead, HeadCell } from '../../../../commons/generics/GenericTableHead';
import { SinDatos } from '../../../../commons/generics/SinDatos';
// import { SurveyImage } from '../../../calificaciones/configuracion-encuestas/ConfiguracionEncuestas';
import { EditRatingsDevice } from './modals/EditRatingsDevice';
import { DetailRatingsDevice } from './modals/DetailRatingsDevice';
import { SurveyImage } from '../../configuracion-encuestas/components/FormSurvey';

export const TableRatingsDevices = () => {
  const dispatch = useDispatch();
  const { devices_list } = useSelector((state: any) => state.ratingsDevices);

  // TABLE STATE HOOK
  const {
    dataTableList,
    canView,
    setCanView,
    canEdit,
    setCanEdit,
    canDelete,
    setCanDelete,
    canRestore,
    setCanRestore,
    openElem,
    setOpenElem,
    checked,
    setChecked,
    searchTerm,
    setSearchTerm,
    order,
    orderBy,
    handleSwitchChange,
    handleSearch: handleSearchHook,
    handleResetSearch: handleResetSearchHook,
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
    pagePagination,
    itemCountPagination,
    changePaginationPage: changePaginationPageHook,
    handleRequestSort,
    updatePaginationFromList,
    openModalDeactivation,
    setOpenModalDeactivation,
    openModalRestore,
    setOpenModalRestore,
    ResponseComponents,
    handleResponse,
    getSortedAndFilteredList,
    isServerSide,
  } = useTableState('qiratingsgdispositivos');

  // Local State
  const [hasPermission, setHasPermission] = useState<boolean>(true);
  const [modalDetalle, setModalDetalle] = useState(false);
  const [modalEditar, setModalEditar] = useState(false);
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    updatePaginationFromList(devices_list);
  }, [devices_list]);

  const changePaginationPage = async (event: React.ChangeEvent<unknown>, value: number) => {
    changePaginationPageHook(event, value);
    const response = await dispatch(getDevicesByPagination(value, checked ? 0 : 1) as any);
    await handleResponse(response, 'listar dispositivos', false);
  };

  const handleSearch = async (param: string) => {
    setSearchTerm(param);
    setInputValue(param);
    handleSearchHook(
      param,
      dispatch,
      (p: string) => getDevicesBySearch(p, checked ? 0 : 1),
      getInitialData,
      null,
      'buscar dispositivos'
    );
  };

  const onSwitchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    handleSwitchChange(e);
    const newChecked = e.target.checked;
    const status = newChecked ? 0 : 1;
    if (searchTerm) {
      handleSearchHook(
        searchTerm,
        dispatch,
        (p: string) => getDevicesBySearch(p, status),
        () => getInitialData(status),
        null,
        'buscar dispositivos'
      );
    } else {
      getInitialData(status);
    }
  };

  const onResetSearch = () => {
    setInputValue('');
    handleResetSearchHook(() => getInitialData());
  };

  useEffect(() => {
    checkPermissions();
    getInitialData();
  }, []);

  const getInitialData = async (currentStatus?: number) => {
    const status = currentStatus !== undefined ? currentStatus : checked ? 0 : 1;
    const response = await dispatch(getDevicesStore(status));
    await handleResponse(response, 'obtener dispositivos', false);
    // if (response && (response.ok || response.code === 200)) {
    //     updatePaginationFromList(response.data || []);
    // }
  };

  const checkPermissions = async () => {
    const view = await hasPemissions(
      'modulo.calificaciones',
      'modulo.calificacion.dispositivos',
      'view'
    );
    if (!view) setHasPermission(false);
    else setCanView(true);

    const edit = await hasPemissions(
      'modulo.calificaciones',
      'modulo.calificacion.dispositivos',
      'edit'
    );
    if (edit) setCanEdit(true);

    const del = await hasPemissions(
      'modulo.calificaciones',
      'modulo.calificacion.dispositivos',
      'delete'
    );
    if (del) setCanDelete(true);

    const rest = await hasPemissions(
      'modulo.calificaciones',
      'modulo.calificacion.dispositivos',
      'restore'
    );
    if (rest) setCanRestore(true);
  };

  const handleView = async (device: any) => {
    const response = await dispatch(storeDetailDevice(device.id));
    await handleResponse(response, 'obtener detalle', false);
    if (response?.ok) {
      setModalDetalle(true);
    }
  };

  const handleEdit = async (device: any) => {
    const response = await dispatch(storeDetailDevice(device.id));
    await handleResponse(response, 'obtener detalle', false);
    if (response?.ok) {
      setModalEditar(true);
    }
  };

  const handleDelete = (device: any) => {
    setOpenElem(device);
    setOpenModalDeactivation(true);
  };

  const handleRestore = (device: any) => {
    setOpenElem(device);
    setOpenModalRestore(true);
  };

  const confirmDeactivate = async () => {
    if (!openElem) return;
    const response = await dispatch(
      storeDesactiveDevice(openElem.id, inputValue, pagePagination, checked ? 0 : 1)
    );
    await handleResponse(response, 'desactivar dispositivo', true);
  };

  const confirmRestore = async () => {
    if (!openElem) return;
    const response = await dispatch(
      storeActiveDevice(openElem.id, inputValue, pagePagination, checked ? 0 : 1)
    );
    await handleResponse(response, 'restaurar dispositivo', true);
  };

  const handleCloseModalDetail = () => {
    setModalDetalle(false);
  };

  const handleCloseModalEditar = () => {
    setModalEditar(false);
  };

  if (!hasPermission) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="error">No tienes permisos para ver este contenido.</Typography>
      </Box>
    );
  }

  const sortedList = getSortedAndFilteredList(
    dataTableList || [],
    ['name', 'description', 'code'],
    'status',
    1
  );

  const headCells: HeadCell[] = [
    { id: 'index', label: '#', sortable: false },
    { id: 'logo', label: 'Logo', sortable: false },
    { id: 'name', label: 'Nombre', sortable: true },
    { id: 'description', label: 'Descripción', sortable: true },
    { id: 'code', label: 'Codigo', sortable: true },
    { id: 'isUserRequired', label: 'Tipo', sortable: true },
    { id: 'status', label: 'Estado', sortable: true },
    { id: 'actions', label: 'Acciones', sortable: false, align: 'center' },
  ];

  return (
    <Box sx={{ width: '100%', mt: 5 }}>
      {ResponseComponents}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
        <TextField
          placeholder="Buscar dispositivo..."
          variant="outlined"
          size="small"
          autoComplete="off"
          value={searchTerm}
          onChange={(e) => handleSearch(e.target.value)}
          sx={{ width: 400 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: inputValue && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={onResetSearch}>
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <Switch checked={checked} onChange={onSwitchChange} color="primary" />
          <Typography variant="body2" sx={{ color: '#4B5563', fontWeight: 500 }}>
            Mostrar solo inactivos (Dispositivo)
          </Typography>
        </Box>
      </Box>

      <Card
        sx={{
          borderRadius: '12px',
          border: '1px solid #E5E7EB',
          boxShadow: 'none',
          overflow: 'hidden',
        }}
      >
        <TableContainer>
          <Table>
            <GenericTableHead
              headCells={headCells}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {sortedList.length === 0 ? (
                <TableRow>
                  <StyledTableCell colSpan={8} align="center">
                    <SinDatos />
                  </StyledTableCell>
                </TableRow>
              ) : (
                sortedList
                  .slice(
                    isServerSide ? 0 : page * rowsPerPage,
                    isServerSide ? rowsPerPage : page * rowsPerPage + rowsPerPage
                  )
                  .map((row: any, index: number) => (
                    <StyledTableRow key={row.id} hover>
                      <StyledTableCell>
                        <Box sx={{ fontWeight: 600, color: 'text.primary' }}>
                          {page * rowsPerPage + index + 1}
                        </Box>
                      </StyledTableCell>
                      <StyledTableCell>
                        <Box
                          sx={{
                            width: 80,
                            height: 80,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '1px solid #eee',
                            borderRadius: 1,
                            overflow: 'hidden',
                            bgcolor: '#fafafa',
                          }}
                        >
                          <SurveyImage
                            imageName={
                              row.logoDevice && typeof row.logoDevice === 'object'
                                ? row.logoDevice.imgName
                                : row.logoDevice
                            }
                            width={80}
                            height={80}
                          />
                        </Box>
                      </StyledTableCell>
                      <StyledTableCell>
                        <ClampedText
                          text={usarFuncionesComposicion(
                            corregirTexto,
                            firstLetterUpperCase,
                            row.name
                          )}
                        />
                      </StyledTableCell>
                      <StyledTableCell>
                        <ClampedText
                          text={usarFuncionesComposicion(
                            corregirTexto,
                            firstLetterUpperCase,
                            row.description
                          )}
                        />
                      </StyledTableCell>
                      <StyledTableCell>
                        <ClampedText text={row.code} />
                      </StyledTableCell>
                      <StyledTableCell>
                        <ClampedText text={row.isUserRequired ? 'Con Usuario' : 'Sin Usuario'} />
                      </StyledTableCell>
                      <StyledTableCell>
                        <StatusChip status={row.status} />
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                          <span style={{ cursor: !canView ? 'not-allowed' : 'pointer' }}>
                            <IconButton
                              size="small"
                              onClick={() => canView && handleView(row)}
                              disabled={!canView}
                              color="info"
                            >
                              <VisibilityOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>

                          <span
                            style={{
                              cursor: !canEdit || row.status !== 1 ? 'not-allowed' : 'pointer',
                            }}
                          >
                            <IconButton
                              size="small"
                              onClick={() => canEdit && row.status === 1 && handleEdit(row)}
                              disabled={!canEdit || row.status !== 1}
                              color="primary"
                            >
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>

                          {row.status === 1 ? (
                            <span style={{ cursor: !canDelete ? 'not-allowed' : 'pointer' }}>
                              <IconButton
                                size="small"
                                onClick={() => canDelete && handleDelete(row)}
                                disabled={!canDelete}
                                color="error"
                              >
                                <DeleteOutlineOutlinedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          ) : (
                            <span style={{ cursor: !canRestore ? 'not-allowed' : 'pointer' }}>
                              <IconButton
                                size="small"
                                onClick={() => canRestore && handleRestore(row)}
                                disabled={!canRestore}
                                color="success"
                              >
                                <RestoreOutlinedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          )}
                        </Box>
                      </StyledTableCell>
                    </StyledTableRow>
                  ))
              )}
            </TableBody>
          </Table>
          {dataTableList.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={dataTableList.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Filas por página"
            />
          )}

          {dataTableList.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: 1,
                padding: 2.5,
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <Pagination
                sx={{ justifyContent: 'center' }}
                color="primary"
                count={itemCountPagination}
                page={pagePagination}
                onChange={changePaginationPage}
                showFirstButton
                showLastButton
                shape="circular"
              />
            </Box>
          )}
        </TableContainer>
      </Card>

      <DetailRatingsDevice open={modalDetalle} handleCloseModalDetail={handleCloseModalDetail} />
      <EditRatingsDevice open={modalEditar} handleCloseModalEditar={handleCloseModalEditar} />

      <ModalConfirmDeactivation
        open={openModalDeactivation}
        setOpen={setOpenModalDeactivation}
        onConfirm={confirmDeactivate}
      />
      <ModalConfirmRestore
        open={openModalRestore}
        setOpen={setOpenModalRestore}
        onConfirm={confirmRestore}
      />
    </Box>
  );
};
